import * as api from "./client.js";

export type QueueOverview = {
  name: string;
  counts: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
    paused: number;
  };
  lastExecutedAt: number | null;
};

export type JobSummary = {
  id: string;
  name: string;
  status: string;
  timestamp: number | null;
  processedOn: number | null;
  finishedOn: number | null;
  attemptsMade: number;
  failedReason: string | null;
  progress: number | string | object;
};

export type JobDetail = JobSummary & {
  data: unknown;
  returnValue: unknown;
  stacktrace: string[];
};

async function getQueuesOverview(): Promise<{ queues: QueueOverview[] }> {
  return api.get<{ queues: QueueOverview[] }>("/v1/content-processor/queues");
}

async function getQueueJobs(
  queueName: string,
  status?: string,
  page?: number,
  pageSize?: number,
): Promise<{ jobs: JobSummary[]; total: number }> {
  const params = new URLSearchParams();
  if (status !== undefined) params.set("status", status);
  if (page) params.set("page", String(page));
  if (pageSize) params.set("pageSize", String(pageSize));

  const query = params.toString();
  const data = await api.get<{ jobs: JobSummary[]; pagination: { total: number } }>(
    `/v1/content-processor/queues/${encodeURIComponent(queueName)}/jobs${query ? `?${query}` : ""}`,
  );
  return { jobs: data.jobs, total: data.pagination.total };
}

async function getJobDetails(queueName: string, jobId: string): Promise<JobDetail> {
  const data = await api.get<{ job: JobDetail }>(
    `/v1/content-processor/queues/${encodeURIComponent(queueName)}/jobs/${encodeURIComponent(jobId)}`,
  );
  return data.job;
}

async function retryJob(queueName: string, jobId: string): Promise<void> {
  await api.post<void>(
    `/v1/content-processor/queues/${encodeURIComponent(queueName)}/jobs/${encodeURIComponent(jobId)}/retry`,
  );
}

async function retryAllFailed(queueName: string): Promise<void> {
  await api.post<void>(
    `/v1/content-processor/queues/${encodeURIComponent(queueName)}/retry-failed`,
  );
}

async function cleanQueue(queueName: string, status: string, grace?: number): Promise<void> {
  await api.post<void>(`/v1/content-processor/queues/${encodeURIComponent(queueName)}/clean`, {
    status,
    grace,
  });
}

export const queueMonitorService = {
  getQueuesOverview,
  getQueueJobs,
  getJobDetails,
  retryJob,
  retryAllFailed,
  cleanQueue,
};
